import {
    Component,
    OnInit,
    ChangeDetectorRef,
    signal,
    Input,
    Output,
    EventEmitter,
    TemplateRef,
    ViewChild,
    ChangeDetectionStrategy,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { Budget } from "../Budget";
import { BudgetService } from "../budget.service";
import { S25Util } from "../../../../util/s25-util";
import { S25ButtonComponent } from "../../../../standalone/s25.button.component";
import { S25LoadingInlineModule } from "../../../s25-loading/s25.loading.inline.module";
import { S25FadingSuccessMsgComponent } from "../../../s25-fading-success-msg/s25.fading.success.msg.component";
import { S25TableModule } from "../../../s25-table/s25.table.module";
import { Table } from "../../../s25-table/Table";
import { Bind } from "../../../../decorators/bind.decorator";
import { CacheRepository } from "../../../../decorators/cache.decorator";
import { GenericTableRenderHTMLComponent } from "../../../s25-table/generics/generic.table.render.html.component";
import { GenericTableButtonComponent } from "../../../s25-table/generics/generic.table.button.component";
import { S25EmojiPickerComponent } from "../../../../standalone/s25.emoji.picker.component";

type CategoryFormData = {
    categoryId?: number;
    name: string;
    description?: string;
    color?: string;
    icon?: string;
    parentCategoryId?: number;
    sortOrder: number;
};

@TypeManagerDecorator("s25-ng-budget-category-management")
@Component({
    selector: "s25-ng-budget-category-management",
    template: `
        <div class="category-management-container">
            <div class="management-header">
                <h3>Budget Category Management</h3>
                <s25-ng-button
                    [type]="'primary'"
                    [buttonText]="'+ Add Category'"
                    [onClick]="startAddCategory"
                    [disabled]="isProcessing()"
                ></s25-ng-button>
            </div>

            <s25-ng-fading-success-msg [message]="successMessage"></s25-ng-fading-success-msg>
            @if (isLoading()) {
                <s25-ng-loading-inline-static [text]="'Loading categories...'"></s25-ng-loading-inline-static>
            }

            <!-- Error State -->
            @if (error) {
                <div class="error-state">
                    <p class="error-message">{{ error }}</p>
                    <s25-ng-button [type]="'outline'" [buttonText]="'Retry'" [onClick]="loadCategories"></s25-ng-button>
                </div>
            }

            <!-- Category Form Modal -->
            @if (showForm()) {
                <div class="modal-overlay" (click)="cancelForm()">
                    <div class="modal-content" (click)="$event.stopPropagation()">
                        <div class="modal-header">
                            <h4>{{ isEditing() ? "Edit Category" : "Add New Category" }}</h4>
                            <button class="close-button" (click)="cancelForm()" type="button">×</button>
                        </div>

                        <form class="category-form" (ngSubmit)="saveCategory()" #categoryForm="ngForm">
                            <div class="form-group">
                                <label for="categoryName">Category Name *</label>
                                <input
                                    id="categoryName"
                                    type="text"
                                    [(ngModel)]="formData.name"
                                    name="categoryName"
                                    required
                                    maxlength="100"
                                    class="form-input"
                                    placeholder="Enter category name"
                                />
                            </div>

                            <div class="form-row">
                                <div class="form-group">
                                    <label for="categoryColor">Color</label>
                                    <input
                                        id="categoryColor"
                                        type="color"
                                        [(ngModel)]="formData.color"
                                        name="categoryColor"
                                        class="form-color-input"
                                    />
                                </div>

                                <div class="form-group">
                                    <label for="categoryIcon">Icon</label>
                                    <s25-ng-emoji-picker
                                        [(value)]="formData.icon"
                                        [placeholder]="'Select icon'"
                                    ></s25-ng-emoji-picker>
                                </div>
                            </div>

                            <div class="form-group">
                                <label for="categoryDescription">Description</label>
                                <textarea
                                    id="categoryDescription"
                                    [(ngModel)]="formData.description"
                                    name="categoryDescription"
                                    class="form-textarea"
                                    placeholder="Optional description"
                                    rows="3"
                                ></textarea>
                            </div>

                            <div class="form-group">
                                <label for="parentCategory">Parent Category</label>
                                <select
                                    id="parentCategory"
                                    [(ngModel)]="formData.parentCategoryId"
                                    name="parentCategory"
                                    class="form-select"
                                >
                                    <option [value]="undefined">No Parent (Top Level)</option>
                                    @for (category of getAvailableParentCategories(); track category.categoryId) {
                                        <option [value]="category.categoryId">
                                            {{ getCategoryDisplayName(category) }}
                                        </option>
                                    }
                                </select>
                            </div>

                            <div class="form-actions">
                                <s25-ng-button
                                    [type]="'outline'"
                                    [buttonText]="'Cancel'"
                                    [onClick]="cancelForm"
                                    [disabled]="isProcessing()"
                                ></s25-ng-button>
                                <s25-ng-button
                                    [type]="'primary'"
                                    [buttonText]="isEditing() ? 'Update Category' : 'Create Category'"
                                    [onClick]="saveCategory"
                                    [isLoading]="isProcessing()"
                                    [disabled]="!categoryForm.valid"
                                ></s25-ng-button>
                            </div>
                        </form>
                    </div>
                </div>
            }

            <!-- Categories Table -->
            @if (!isLoading() && !error) {
                <s25-ng-table
                    [dataSource]="categoryTableData"
                    [hasRefresh]="true"
                    [hasFilter]="true"
                    [columnSortable]="true"
                    [hasTotalRowCount]="true"
                    [hasExpandableRows]="true"
                    [caption]="'Budget Categories'"
                    [noResultsMessage]="
                        'No categories have been created yet. Click Add Category to create your first budget category.'
                    "
                ></s25-ng-table>
            }

            <!-- Delete Confirmation Modal -->
            @if (showDeleteConfirm()) {
                <div class="modal-overlay" (click)="cancelDelete()">
                    <div class="modal-content small" (click)="$event.stopPropagation()">
                        <div class="modal-header">
                            <h4>Confirm Delete</h4>
                        </div>
                        <div class="modal-body">
                            <p>Are you sure you want to delete the category "{{ categoryToDelete()?.name }}"?</p>
                            <p class="warning-text">This action cannot be undone.</p>
                        </div>
                        <div class="modal-actions">
                            <s25-ng-button
                                [type]="'outline'"
                                [buttonText]="'Cancel'"
                                [onClick]="cancelDelete"
                            ></s25-ng-button>
                            <s25-ng-button
                                [type]="'danger'"
                                [buttonText]="'Delete'"
                                [onClick]="deleteCategory"
                                [isLoading]="isProcessing()"
                            ></s25-ng-button>
                        </div>
                    </div>
                </div>
            }
        </div>

        <!-- Template for subcategory expanded content -->
        <ng-template #subcategoryTemplate let-data let-row="row">
            <div class="subcategories-expanded">
                @for (subcategory of data.subcategories; track subcategory.categoryId) {
                    <div class="subcategory-item-expanded">
                        <div class="subcategory-info">
                            <span class="tree-connector">└─</span>
                            @if (subcategory.color) {
                                <div
                                    class="subcategory-color"
                                    [style.background-color]="subcategory.color"
                                    [title]="subcategory.color"
                                ></div>
                            }
                            @if (subcategory.icon) {
                                <span class="subcategory-icon">{{ subcategory.icon }}</span>
                            }
                            <span class="subcategory-name">{{ subcategory.name }}</span>
                            @if (subcategory.description) {
                                <span class="subcategory-description">{{ subcategory.description }}</span>
                            }
                        </div>
                        <div class="subcategory-actions">
                            <s25-ng-button
                                [type]="'outline'"
                                [buttonText]="'Edit'"
                                [onClick]="startEditCategory"
                                [onClickArgs]="[subcategory]"
                                [disabled]="isProcessing()"
                            ></s25-ng-button>
                            <s25-ng-button
                                [type]="'danger--outline'"
                                [buttonText]="'Delete'"
                                [onClick]="confirmDeleteCategory"
                                [onClickArgs]="[subcategory]"
                                [disabled]="isProcessing()"
                            ></s25-ng-button>
                        </div>
                    </div>
                }
            </div>
        </ng-template>
    `,
    styles: `
        .category-management-container {
            padding: 1.5rem;
        }

        .management-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1.5rem;
            padding-bottom: 1rem;
            border-bottom: 1px solid #e5e5e5;
        }

        .management-header h3 {
            margin: 0;
            color: #333;
            font-size: 1.25rem;
            font-weight: 600;
        }

        .error-state {
            text-align: center;
            padding: 2rem;
            background: #fff3f3;
            border: 1px solid #ffcdd2;
            border-radius: 8px;
            margin: 1rem 0;
        }

        .error-message {
            color: #d32f2f;
            margin-bottom: 1rem;
        }

        /* Modal Styles */
        .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
        }

        .modal-content {
            background: white;
            border-radius: 8px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
            max-width: 600px;
            width: 90%;
            max-height: 90vh;
            overflow-y: auto;
        }

        .modal-content.small {
            max-width: 400px;
        }

        .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1.5rem;
            border-bottom: 1px solid #e5e5e5;
        }

        .modal-header h4 {
            margin: 0;
            color: #333;
            font-size: 1.1rem;
            font-weight: 600;
        }

        .close-button {
            background: none;
            border: none;
            font-size: 1.5rem;
            cursor: pointer;
            color: #666;
            padding: 0;
            width: 30px;
            height: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .close-button:hover {
            color: #333;
        }

        /* Form Styles */
        .category-form {
            padding: 1.5rem;
        }

        .form-group {
            margin-bottom: 1rem;
        }

        .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 1rem;
        }

        .form-group label {
            display: block;
            margin-bottom: 0.5rem;
            font-weight: 500;
            color: #333;
        }

        .form-input,
        .form-textarea,
        .form-select {
            width: 100%;
            padding: 0.75rem;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 1rem;
            transition: border-color 0.2s;
        }

        .form-input:focus,
        .form-textarea:focus,
        .form-select:focus {
            outline: none;
            border-color: #2196f3;
            box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
        }

        .form-color-input {
            width: 60px;
            height: 40px;
            padding: 0;
            border: 1px solid #ddd;
            border-radius: 4px;
            cursor: pointer;
        }

        .form-actions {
            display: flex;
            justify-content: flex-end;
            gap: 1rem;
            margin-top: 1.5rem;
            padding-top: 1rem;
            border-top: 1px solid #e5e5e5;
        }

        /* Table Styles */
        .category-hierarchy-cell {
            font-family: monospace;
        }

        .parent-category-row {
            font-weight: 600;
            color: #333;
        }

        .subcategory-row {
            color: #666;
            font-size: 0.95rem;
        }

        /* Expanded Subcategories Styles */
        .subcategories-expanded {
            background: #f9f9f9;
            border: 1px solid #e5e5e5;
            border-radius: 4px;
            margin: 8px;
        }

        .subcategory-item-expanded {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 12px 16px;
            border-bottom: 1px solid #f0f0f0;
        }

        .subcategory-item-expanded:last-child {
            border-bottom: none;
        }

        .subcategory-info {
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .tree-connector {
            color: #999;
            font-family: monospace;
            margin-right: 4px;
        }

        .subcategory-color {
            width: 16px;
            height: 16px;
            border-radius: 50%;
            border: 1px solid #ddd;
        }

        .subcategory-icon {
            font-size: 1rem;
        }

        .subcategory-name {
            font-weight: 500;
            color: #333;
        }

        .subcategory-description {
            color: #666;
            font-size: 0.9rem;
            margin-left: 8px;
        }

        .subcategory-actions {
            display: flex;
            gap: 8px;
        }

        /* Action buttons in table */
        .category-actions-row {
            display: flex;
            gap: 4px;
            justify-content: center;
        }

        /* Modal Body and Actions */
        .modal-body {
            padding: 1.5rem;
        }

        .modal-actions {
            display: flex;
            justify-content: flex-end;
            gap: 1rem;
            padding: 1rem 1.5rem;
            border-top: 1px solid #e5e5e5;
        }

        .warning-text {
            color: #f44336;
            font-weight: 500;
            margin-top: 0.5rem;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
            .category-management-container {
                padding: 1rem;
            }

            .management-header {
                flex-direction: column;
                gap: 1rem;
                align-items: stretch;
            }

            .modal-content {
                width: 95%;
                margin: 1rem;
            }

            .form-row {
                grid-template-columns: 1fr;
            }

            .category-header {
                flex-direction: column;
                gap: 1rem;
                align-items: stretch;
            }

            .category-actions {
                justify-content: center;
                flex-wrap: wrap;
            }

            .subcategory-item {
                flex-direction: column;
                gap: 1rem;
                align-items: stretch;
                padding-left: 2rem;
            }

            .subcategory-item .category-actions {
                justify-content: center;
            }
        }
    `,
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        S25ButtonComponent,
        S25LoadingInlineModule,
        S25FadingSuccessMsgComponent,
        S25TableModule,
        S25EmojiPickerComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BudgetCategoryManagementComponent implements OnInit {
    @ViewChild("subcategoryTemplate", { static: true }) subcategoryTemplate!: TemplateRef<any>;
    @Output() categoriesChanged = new EventEmitter<void>();

    // Signals for reactive state management
    categories = signal<Budget.BudgetCategory[]>([]);
    isLoading = signal<boolean>(false);
    isProcessing = signal<boolean>(false);
    showForm = signal<boolean>(false);
    isEditing = signal<boolean>(false);
    showDeleteConfirm = signal<boolean>(false);
    categoryToDelete = signal<Budget.BudgetCategory | null>(null);
    successMessage = signal<string>("");

    // Component state
    error: string | null = null;
    expandedCategories = new Set<number>();

    // Form data
    formData: CategoryFormData = {
        name: "",
        description: "",
        color: "#2196f3",
        icon: "",
        parentCategoryId: undefined,
        sortOrder: 0,
    };

    // Table data source - initialized in constructor
    categoryTableData!: Table.DataSource;

    constructor(private cd: ChangeDetectorRef) {}

    async ngOnInit(): Promise<void> {
        this.initializeTableData();
        await this.loadCategories();
    }

    private initializeTableData(): void {
        this.categoryTableData = {
            type: "unpaginated",
            dataSource: this.getCategoryTableRows,
            columns: [
                {
                    id: "hierarchy",
                    header: "Category",
                    minWidth: 250,
                    cells: {
                        text: "",
                        className: "category-hierarchy-cell",
                    },
                },
                {
                    id: "description",
                    header: "Description",
                    minWidth: 200,
                    cells: {
                        text: "",
                        className: "text-muted",
                    },
                },
                {
                    id: "icon",
                    header: "Icon",
                    width: 80,
                    align: "center",
                    cells: {
                        text: "",
                    },
                },
                {
                    id: "color",
                    header: "Color",
                    width: 80,
                    align: "center",
                    cells: {
                        text: "",
                    },
                },
                {
                    id: "subcategories",
                    header: "Subcategories",
                    width: 120,
                    align: "center",
                    cells: {
                        text: "",
                    },
                },
                GenericTableButtonComponent.Column(
                    "Add Sub",
                    (row: Table.Row) => this.startAddSubCategory(row.data as Budget.BudgetCategory),
                    "outline",
                    80,
                ),
                GenericTableButtonComponent.Column(
                    "Edit",
                    (row: Table.Row) => this.startEditCategory(row.data as Budget.BudgetCategory),
                    "outline",
                    60,
                ),
                {
                    id: "delete",
                    header: "Delete",
                    width: 70,
                    align: "center",
                    sortable: false,
                    cells: {
                        component: GenericTableButtonComponent,
                        inputs: {
                            label: "Delete",
                            type: "danger--outline",
                        },
                    },
                },
            ],
        };
    }

    async loadCategories(): Promise<void> {
        this.isLoading.set(true);
        this.error = null;

        try {
            // Invalidate cache to ensure fresh data
            CacheRepository.invalidateByService("BudgetService", "getBudgetCategories");
            const [categories, error] = await S25Util.Maybe(BudgetService.getBudgetCategories());

            if (error) {
                this.error = "Failed to load categories. Please try again.";
                console.error("Error loading categories:", error);
            } else {
                this.categories.set(categories || []);
            }
        } catch (error) {
            this.error = "An unexpected error occurred while loading categories.";
            console.error("Unexpected error:", error);
        } finally {
            this.isLoading.set(false);
        }
    }

    startAddCategory = () => {
        this.resetForm();
        this.isEditing.set(false);
        this.showForm.set(true);
    };

    startAddSubCategory = (parentCategory: Budget.BudgetCategory) => {
        this.resetForm();
        this.formData.parentCategoryId = parentCategory.categoryId;
        this.isEditing.set(false);
        this.showForm.set(true);
    };

    startEditCategory = (category: Budget.BudgetCategory) => {
        this.formData = {
            categoryId: category.categoryId,
            name: category.name,
            description: category.description || "",
            color: category.color || "#2196f3",
            icon: category.icon || "",
            parentCategoryId: category.parentCategoryId,
            sortOrder: category.sortOrder,
        };
        this.isEditing.set(true);
        this.showForm.set(true);
    };

    cancelForm = () => {
        this.showForm.set(false);
        this.resetForm();
    };

    async saveCategory(): Promise<void> {
        if (!this.formData.name.trim()) {
            return;
        }

        this.isProcessing.set(true);

        try {
            const categoryData: Partial<Budget.BudgetCategory> = {
                ...this.formData,
                name: this.formData.name.trim(),
                isActive: 1, // Active by default
            };

            if (this.isEditing()) {
                await BudgetService.updateBudgetCategory(this.formData.categoryId!, categoryData);
                this.successMessage.set("Category updated successfully");
            } else {
                await BudgetService.createBudgetCategory(categoryData as Budget.BudgetCategory);
                this.successMessage.set("Category created successfully");
            }

            this.showForm.set(false);
            this.resetForm();
            await this.loadCategories();
            this.refreshTable();
            this.categoriesChanged.emit();
        } catch (error) {
            console.error("Error saving category:", error);
            this.error = "Failed to save category. Please try again.";
        } finally {
            this.isProcessing.set(false);
        }
    }

    confirmDeleteCategory = (category: Budget.BudgetCategory) => {
        this.categoryToDelete.set(category);
        this.showDeleteConfirm.set(true);
    };

    cancelDelete(): void {
        this.showDeleteConfirm.set(false);
        this.categoryToDelete.set(null);
    }

    async deleteCategory(): Promise<void> {
        const category = this.categoryToDelete();
        if (!category) return;

        this.isProcessing.set(true);

        try {
            await BudgetService.deleteBudgetCategory(category.categoryId);
            this.successMessage.set("Category deleted successfully");
            this.showDeleteConfirm.set(false);
            this.categoryToDelete.set(null);
            await this.loadCategories();
            this.refreshTable();
            this.categoriesChanged.emit();
        } catch (error) {
            console.error("Error deleting category:", error);
            this.error = "Failed to delete category. Please try again.";
        } finally {
            this.isProcessing.set(false);
        }
    }

    @Bind
    async getCategoryTableRows(): Promise<Table.DataSourceResponse> {
        // Invalidate cache to ensure fresh data
        CacheRepository.invalidateByService("BudgetService", "getBudgetCategories");
        const categories = this.categories();
        const rows: Table.Row[] = [];

        // Only show top-level categories, subcategories will be in expandable content
        const topLevelCategories = categories
            .filter((category) => !category.parentCategoryId)
            .sort((a, b) => a.sortOrder - b.sortOrder);

        for (const category of topLevelCategories) {
            rows.push(this.createCategoryRow(category, categories));
        }

        return {
            rows,
            totalRows: rows.length,
        };
    }

    private createCategoryRow(category: Budget.BudgetCategory, allCategories: Budget.BudgetCategory[]): Table.Row {
        const subcategories = allCategories.filter((c) => c.parentCategoryId === category.categoryId);
        const subcategoryCount = subcategories.length;
        const hasSubcategories = subcategoryCount > 0;

        console.log(
            "Category:",
            category.name,
            "Has subcategories:",
            hasSubcategories,
            "Subcategories:",
            subcategories,
        );

        const expandedContent = hasSubcategories ? this.createSubcategoryExpandedContent(subcategories) : undefined;
        console.log("Expanded content:", expandedContent);

        return {
            id: category.categoryId,
            name: category.name,
            data: category,
            expandable: hasSubcategories,
            expandedContent: expandedContent,
            cells: {
                hierarchy: {
                    text: category.name,
                    className: "parent-category-row",
                },
                description: {
                    text: category.description || "",
                },
                icon: {
                    text: category.icon || "",
                },
                color: {
                    component: category.color ? GenericTableRenderHTMLComponent : undefined,
                    inputs: category.color
                        ? {
                              text: `<div style="width: 20px; height: 20px; background-color: ${category.color}; border: 1px solid #ddd; border-radius: 50%; margin: 0 auto;" title="${category.color}"></div>`,
                              isHtml: true,
                          }
                        : undefined,
                    text: category.color || "",
                },
                subcategories: {
                    text: subcategoryCount.toString(),
                },
                delete: {
                    inputs: {
                        label: "Delete",
                        disabled: hasSubcategories,
                        onClick: () => this.confirmDeleteCategory(category),
                    },
                },
            },
        };
    }

    private createSubcategoryExpandedContent(subcategories: Budget.BudgetCategory[]): Table.ExpandedContent {
        console.log("Creating expanded content for subcategories:", subcategories);

        return {
            template: this.subcategoryTemplate,
            data: { subcategories: subcategories },
            height: "auto",
        };
    }

    // Helper methods
    private resetForm(): void {
        this.formData = {
            name: "",
            description: "",
            color: "#2196f3",
            icon: "",
            parentCategoryId: undefined,
            sortOrder: this.getNextSortOrder(),
        };
    }

    private getNextSortOrder(): number {
        const categories = this.categories();
        return categories.length > 0 ? Math.max(...categories.map((c) => c.sortOrder)) + 1 : 1;
    }

    hasSubCategories(category: Budget.BudgetCategory): boolean {
        return this.categories().some((c) => c.parentCategoryId === category.categoryId);
    }

    hasSubcategoriesForRow(category: Budget.BudgetCategory): boolean {
        return this.categories().some((c) => c.parentCategoryId === category.categoryId);
    }

    getAvailableParentCategories(): Budget.BudgetCategory[] {
        const currentId = this.formData.categoryId;
        return this.categories()
            .filter(
                (category) =>
                    !category.parentCategoryId && // Only top-level categories can be parents
                    category.categoryId !== currentId, // Can't be parent of itself
            )
            .sort((a, b) => a.sortOrder - b.sortOrder);
    }

    getCategoryDisplayName(category: Budget.BudgetCategory): string {
        return `${category.icon ? category.icon + " " : ""}${category.name}`;
    }

    private refreshTable(): void {
        this.categoryTableData = {
            ...this.categoryTableData,
            dataSource: this.getCategoryTableRows,
        };
        this.cd.detectChanges();
    }
}
